// Pure helpers for the Workshop 5 dashboard (mirrors workshop1-4Data.js style).
// Residual reads follow the RR card display logic exactly
// (RiskScenarioListView.js:1039-1047): per attack path in attackPathScenarios,
// find rs = riskScenarios.find(r => r.attackPathId === ap.id) — residual gravity
// = Number(rs?.residualRisk?.residualGravity) || ap.gravity, residual likelihood
// = Number(rs?.residualRisk?.residualLikelihood) || ap.likelihood. Unset residual
// renders as initial (no-change bars), matching the page pixel-for-pixel.
// Rows carry a `fallback` flag (true when residual fell back to initial) so the
// charts can name fallback rows. Only rows missing even initial values are
// reported as unmapped — never zero, never blank.

export const W5_TREATMENT_OPTIONS = ['reduce', 'accept', 'transfer', 'avoid'];

export const W5_EXCLUDED_IMPLEMENTATIONS = ['not_planned', 'abandoned', 'implemented'];

// Hex equivalents of the risk-scale color names (green/yellow/orange/red) used
// by the riskLevels config, for ECharts series colors.
export const W5_RISK_COLOR_HEX = {
  green: '#10B981',
  yellow: '#F59E0B',
  orange: '#F97316',
  red: '#DC2626'
};

export const W5_OPTION_COLORS = {
  reduce: '#3B82F6',
  accept: '#F59E0B',
  transfer: '#8B5CF6',
  avoid: '#EF4444'
};

export const resolveAttackPathId = (attackPath) => (
  typeof attackPath === 'string' ? attackPath : String(attackPath?.id ?? '')
);

const toPositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

// Card-parity residual resolution (RR cards, RiskScenarioListView.js:1039-1047).
// Initial = ap.gravity x ap.likelihood; residual comes from the riskScenarios
// join (store getRiskScenarios()) with the card's own fallback — unset residual
// renders as initial. `fallback` is true when either residual value fell back.
export const resolveCardResidual = (attackPath, riskScenarios) => {
  const list = Array.isArray(riskScenarios) ? riskScenarios : [];
  const rs = list.find((r) => r?.attackPathId === attackPath?.id);
  const residual = rs?.residualRisk || {};
  const initialGravity = toPositiveNumber(attackPath?.gravity);
  const initialLikelihood = toPositiveNumber(attackPath?.likelihood);
  const residualGravity = Number(residual.residualGravity) || initialGravity;
  const residualLikelihood = Number(residual.residualLikelihood) || initialLikelihood;
  const fallback = !Number(residual.residualGravity) || !Number(residual.residualLikelihood);
  return { initialGravity, initialLikelihood, residualGravity, residualLikelihood, fallback };
};

// First-owner lookup: attack-path id -> owning strategic scenario.
export const buildOwnerById = (strategicScenarios) => {
  const ownerById = new Map();
  (Array.isArray(strategicScenarios) ? strategicScenarios : []).forEach((ss) => {
    (Array.isArray(ss?.attackPaths) ? ss.attackPaths : []).forEach((ap) => {
      const apId = resolveAttackPathId(ap);
      if (apId && !ownerById.has(apId)) {
        ownerById.set(apId, ss);
      }
    });
  });
  return ownerById;
};

// The ONLY unmapped case under card parity: rows missing even initial values.
// Returns { reason, bad } or null when usable. Residual is always usable —
// unset residual falls back to initial (see resolveCardResidual), never excluded.
export const classifyUnmappedPath = ({ initialGravity, initialLikelihood }) => {
  if (initialGravity === null || initialLikelihood === null) {
    const bad = [];
    if (initialGravity === null) {
      bad.push('gravity');
    }
    if (initialLikelihood === null) {
      bad.push('likelihood');
    }
    return { reason: 'no-initial', bad };
  }
  return null;
};

// One row per risk scenario (= attack path, refs R{n} from the recap table):
// initial = ap.gravity x ap.likelihood, residual = card-parity join
// (resolveCardResidual) with fallback to initial when unset. No MAX-aggregation.
// Top-15 by initial, then sorted by residual desc. Rows missing even initial
// values are excluded from rows and reported via unmappedCount — never rendered
// as zero. `strategicScenarios` is kept for call compatibility (unused).
// `limit` also accepts the riskScenarios array for call compatibility.
export const buildRiskScenarioBars = (attackPathScenarios, strategicScenarios, riskScenarioIndexMap, limit = 15, riskScenarios = undefined) => {
  void strategicScenarios;
  const paths = Array.isArray(attackPathScenarios) ? attackPathScenarios : [];
  const indexMap = riskScenarioIndexMap || {};
  const effectiveLimit = Array.isArray(limit) ? 15 : limit;
  const join = Array.isArray(limit) ? limit : (Array.isArray(riskScenarios) ? riskScenarios : []);
  const rows = [];
  const unmappedDetails = [];
  const fallbackRefs = [];
  paths.forEach((ap) => {
    if (!ap?.id) {
      return;
    }
    const ref = indexMap[ap.id] || {};
    const shortRef = ref.shortRef || ap.id;
    const { initialGravity, initialLikelihood, residualGravity, residualLikelihood, fallback } = resolveCardResidual(ap, join);
    const unmapped = classifyUnmappedPath({ initialGravity, initialLikelihood });
    if (unmapped) {
      unmappedDetails.push({ shortRef, ...unmapped });
      return;
    }
    const code = ap.referenceCode || ref.originalRef || '';
    if (fallback) {
      fallbackRefs.push(shortRef);
    }
    rows.push({
      id: ap.id,
      name: ap.name || ap.id,
      referenceCode: ap.referenceCode || '',
      shortRef,
      shortLabel: code ? `${code} · ${shortRef}` : shortRef,
      initialGravity,
      initialLikelihood,
      initialScore: initialGravity * initialLikelihood,
      residualGravity,
      residualLikelihood,
      residualScore: residualGravity * residualLikelihood,
      fallback
    });
  });
  const top = [...rows]
    .sort((a, b) => b.initialScore - a.initialScore)
    .slice(0, effectiveLimit);
  top.sort((a, b) => b.residualScore - a.residualScore || b.initialScore - a.initialScore);
  top.total = rows.length;
  top.unmappedCount = unmappedDetails.length;
  top.unmappedDetails = unmappedDetails;
  top.fallbackRefs = fallbackRefs;
  return top;
};

export const resolveRiskColorHex = (score, riskLevels) => {
  const numeric = Number(score);
  const level = (Array.isArray(riskLevels) ? riskLevels : []).find(
    (lv) => numeric >= lv.minScore && numeric <= lv.maxScore
  );
  if (level && W5_RISK_COLOR_HEX[level.color]) {
    return W5_RISK_COLOR_HEX[level.color];
  }
  if (numeric >= 12) {
    return W5_RISK_COLOR_HEX.red;
  }
  if (numeric >= 8) {
    return W5_RISK_COLOR_HEX.orange;
  }
  if (numeric >= 4) {
    return W5_RISK_COLOR_HEX.yellow;
  }
  return W5_RISK_COLOR_HEX.green;
};

export const resolveRiskAcceptable = (score, riskLevels) => {
  const numeric = Number(score);
  const level = (Array.isArray(riskLevels) ? riskLevels : []).find(
    (lv) => numeric >= lv.minScore && numeric <= lv.maxScore
  );
  return level ? level.acceptable !== false : null;
};

// Residual matrix off the SAME per-path residual values as the grouped bars
// (card parity): gravity 1..maxGravity x likelihood 1..4, cell = residual
// scenario count. Rows missing even initial values are excluded and reported
// via unmappedDetails ({ shortRef, reason, bad }) — never zero-cells.
// `strategicScenarios` is kept for call compatibility (unused).
export const buildResidualMatrix = (strategicScenarios, attackPathScenarios, maxGravity, riskScenarioIndexMap, riskScenarios = undefined) => {
  void strategicScenarios;
  const paths = Array.isArray(attackPathScenarios) ? attackPathScenarios : [];
  const indexMap = riskScenarioIndexMap || {};
  const join = Array.isArray(riskScenarios) ? riskScenarios : [];
  const rows = Number.isInteger(maxGravity) && maxGravity > 0 ? maxGravity : 5;
  const counts = {};
  let max = 0;
  let total = 0;
  const unmappedDetails = [];
  const fallbackRefs = [];
  paths.forEach((ap) => {
    if (!ap?.id) {
      return;
    }
    const ref = indexMap[ap.id] || {};
    const shortRef = ref.shortRef || ap.id;
    const { initialGravity, initialLikelihood, residualGravity, residualLikelihood, fallback } = resolveCardResidual(ap, join);
    const unmapped = classifyUnmappedPath({ initialGravity, initialLikelihood });
    if (unmapped) {
      unmappedDetails.push({ shortRef, ...unmapped });
      return;
    }
    const g = Math.trunc(residualGravity);
    const l = Math.trunc(residualLikelihood);
    if (g < 1 || g > rows || l < 1 || l > 4) {
      unmappedDetails.push({ shortRef, reason: 'no-initial', bad: [] });
      return;
    }
    if (fallback) {
      fallbackRefs.push(shortRef);
    }
    const key = `${l}|${g}`;
    counts[key] = (counts[key] || 0) + 1;
    if (counts[key] > max) {
      max = counts[key];
    }
    total += 1;
  });
  const unmappedCount = unmappedDetails.length;
  const data = [];
  for (let g = 1; g <= rows; g += 1) {
    for (let l = 1; l <= 4; l += 1) {
      data.push([l - 1, g - 1, counts[`${l}|${g}`] || 0]);
    }
  }
  const xLabels = ['1', '2', '3', '4'];
  const yLabels = [];
  for (let g = 1; g <= rows; g += 1) {
    yLabels.push(String(g));
  }
  return { xLabels, yLabels, data, max, total, unmappedCount, unmappedDetails, fallbackRefs };
};

// Treatment-option distribution over the 4-option enum, counted per risk
// scenario (= attack path in attackPathScenarios, same row identity as the
// grouped bars). The option is read from the owning strategic scenario's
// attackPathTreatments[apId]. Empty options -> untreated note (never a slice);
// any 5th value -> other note (bars revisit, not built).
export const buildOptionDistributionByAttackPath = (attackPathScenarios, strategicScenarios) => {
  const paths = Array.isArray(attackPathScenarios) ? attackPathScenarios : [];
  const scenarios = Array.isArray(strategicScenarios) ? strategicScenarios : [];
  const distribution = { reduce: 0, accept: 0, transfer: 0, avoid: 0, untreated: 0, other: 0, total: 0 };
  const ownerById = new Map();
  scenarios.forEach((ss) => {
    (Array.isArray(ss?.attackPaths) ? ss.attackPaths : []).forEach((ap) => {
      const apId = resolveAttackPathId(ap);
      if (apId && !ownerById.has(apId)) {
        ownerById.set(apId, ss);
      }
    });
  });
  paths.forEach((ap) => {
    if (!ap?.id) {
      return;
    }
    distribution.total += 1;
    const option = ownerById.get(ap.id)?.attackPathTreatments?.[ap.id]?.option || '';
    if (!option) {
      distribution.untreated += 1;
    } else if (W5_TREATMENT_OPTIONS.includes(option)) {
      distribution[option] += 1;
    } else {
      distribution.other += 1;
    }
  });
  return distribution;
};

const normalizeMeasureCategory = (raw) => {
  const text = String(raw || '').trim().toLowerCase();
  if (!text) {
    return 'other';
  }
  if (['governance', 'gouvernance', 'gouvernance et anticipation'].includes(text)) {
    return 'governance';
  }
  if (['defense', 'défense', 'defence'].includes(text)) {
    return 'defense';
  }
  if (['resilience', 'résilience'].includes(text)) {
    return 'resilience';
  }
  if (['protection'].includes(text)) {
    return 'protection';
  }
  return 'other';
};

// Deduped measure rollup across the three measure arrays. Excludes
// not_planned/abandoned/implemented and non-retained measures.
export const collectDedupedMeasures = (strategicScenarios) => {
  const scenarios = Array.isArray(strategicScenarios) ? strategicScenarios : [];
  const seen = new Map();
  scenarios.forEach((ss) => {
    [ss?.customMeasures, ss?.dreadedEventMeasures, ss?.ecosystemMeasures].forEach((arr) => {
      (Array.isArray(arr) ? arr : []).forEach((m) => {
        if (!m || !m.id || seen.has(m.id) || m.retained === false) {
          return;
        }
        if (W5_EXCLUDED_IMPLEMENTATIONS.includes(m.implementation)) {
          return;
        }
        seen.set(m.id, {
          id: m.id,
          name: m.name || m.title || m.measureName || '',
          budget: Number(m.budget) || 0,
          implementation: m.implementation || '',
          deadline: m.deadline || '',
          category: normalizeMeasureCategory(m.category)
        });
      });
    });
  });
  return [...seen.values()];
};

const startOfToday = (now) => {
  const reference = now instanceof Date ? now : new Date(now || Date.now());
  const start = new Date(reference);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
};

// Overdue = valid deadline in the past on a still-pending measure (budget and
// lateness stay separate channels).
export const isOverdueMeasure = (measure, now) => {
  if (!measure?.deadline || W5_EXCLUDED_IMPLEMENTATIONS.includes(measure.implementation)) {
    return false;
  }
  const target = new Date(measure.deadline.length === 10 ? `${measure.deadline}T00:00:00` : measure.deadline).getTime();
  if (Number.isNaN(target)) {
    return false;
  }
  return target < startOfToday(now);
};

// Budget by category: length = deduped budget EUR, sorted desc. Overdue bars
// get an outline marker (same principle as W4 defaulted cells).
export const buildBudgetByCategory = (strategicScenarios, now) => {
  const measures = collectDedupedMeasures(strategicScenarios);
  const byCategory = new Map();
  let totalBudget = 0;
  let overdueCount = 0;
  measures.forEach((m) => {
    const overdue = isOverdueMeasure(m, now);
    if (overdue) {
      overdueCount += 1;
    }
    totalBudget += m.budget;
    const current = byCategory.get(m.category) || { category: m.category, total: 0, count: 0, overdueCount: 0 };
    current.total += m.budget;
    current.count += 1;
    if (overdue) {
      current.overdueCount += 1;
    }
    byCategory.set(m.category, current);
  });
  const rows = [...byCategory.values()]
    .map((row) => ({ ...row, overdue: row.overdueCount > 0 }))
    .sort((a, b) => b.total - a.total || b.count - a.count);
  return { rows, totalBudget, overdueCount, measureCount: measures.length };
};

// Treatment coverage % as KPI (not a chart): risk scenarios with a treatment
// option over all risk scenarios.
export const buildTreatmentCoverage = (strategicScenarios, attackPathScenarios) => {
  const distribution = buildOptionDistributionByAttackPath(attackPathScenarios, strategicScenarios);
  const treated = distribution.reduce + distribution.accept + distribution.transfer + distribution.avoid + distribution.other;
  const total = distribution.total;
  return {
    treated,
    untreated: distribution.untreated,
    total,
    percentage: total === 0 ? 0 : Math.round((treated / total) * 100)
  };
};
